import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone, Mail } from "lucide-react";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Products & Services", path: "/products" },
    { name: "Projects", path: "/projects" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      {/* Top Contact Bar */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 md:gap-6">
            <a href="tel:+60327790507" className="flex items-center space-x-1 sm:space-x-2 hover:text-blue-600 transition-colors">
              <Phone className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="text-xs sm:text-sm">Office: +603 2779 0507</span>
            </a>
            <a href="https://wa.me/60192177871" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 sm:space-x-2 hover:text-green-600 transition-colors">
              <Phone className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="text-xs sm:text-sm">WhatsApp: +60 19 2177871</span>
            </a>
            <a href="mailto:sales@g5petro.com.my" className="hidden sm:flex items-center space-x-2 hover:text-blue-600 transition-colors">
              <Mail className="h-4 w-4" />
              <span>sales@g5petro.com.my</span>
            </a>
          </div>
          <div className="hidden md:block">
            <span>Ampang Jaya, Selangor | Serving Malaysia & Southeast Asia | Terengganu | Johor | Sabah | Sarawak</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            {/* Official G5 Petroleum Logo */}
            <img 
              src="/images/g5_petroleum_logo_1.jpeg" 
              alt="G5 Petroleum Logo" 
              className="h-12 w-12 sm:h-16 sm:w-16 lg:h-18 lg:w-18 object-contain"
            />
            <div className="min-w-0">
              <div className="font-bold text-lg sm:text-xl text-gray-900 truncate">G5 Petroleum Sdn Bhd</div>
              <div className="text-xs text-gray-500 hidden sm:block">PETRONAS Reg: 202201037944</div>
              <div className="text-xs sm:text-sm text-gray-600">Engineering & Marine Solutions</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`font-medium transition-colors hover:text-blue-600 ${
                  isActive(item.path) ? "text-blue-600" : "text-gray-700"
                }`}
              >
                {item.name}
              </Link>
            ))}
            <Button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800">
              Request Quote
            </Button>
          </div>

          {/* Mobile Navigation */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="outline" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <div className="flex flex-col space-y-4 mt-8">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`font-medium py-2 px-4 rounded transition-colors ${
                      isActive(item.path)
                        ? "bg-blue-100 text-blue-600"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <Button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 mt-4">
                  Request Quote
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;